/**
 * Ordnance kit: the shared vocabulary for projectile models — fuselages with
 * panel lines, nose shapes, swept fins, flared nozzles, hazard bands, sensor
 * ports, rings and dishes. Each weapon builder composes these and adds its
 * signature payload. Units are design-scale (×3 game); +Z is forward, the
 * nozzle sits at negative Z.
 */

import * as THREE from 'three';

export const ORD = {
  darkMetal: '#3a3c44',
  lightMetal: '#c8ccd4',
  throat: '#1a1418',
  hazard: '#2a2a30',
  solar: '#243454',
  glass: '#1c222c',
};

export function tint(hex: string, amount: number): THREE.Color {
  const c = new THREE.Color(hex);
  if (amount > 0) c.lerp(new THREE.Color('#ffffff'), amount);
  else c.lerp(new THREE.Color('#000000'), -amount);
  return c;
}
export const mat = (c: string | THREE.Color) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c as string) });
export const matE = (c: string | THREE.Color, i = 0.5) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c as string), emissive: new THREE.Color(c as string), emissiveIntensity: i });
export function at<T extends THREE.Object3D>(o: T, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): T { o.position.set(x, y, z); o.rotation.set(rx, ry, rz); return o; }
export function group(...children: THREE.Object3D[]): THREE.Group { const g = new THREE.Group(); g.add(...children); return g; }

/** Cylinder along +Z (three's cylinders run along Y; we rotate). */
export function tube(rFront: number, rBack: number, len: number, m: THREE.Material, z: number, segs = 14): THREE.Mesh {
  return at(new THREE.Mesh(new THREE.CylinderGeometry(rFront, rBack, len, segs), m), 0, 0, z, Math.PI / 2);
}
/** Ring around the Z axis. */
export function ring(r: number, t: number, m: THREE.Material, z: number, segs = 20): THREE.Mesh {
  return at(new THREE.Mesh(new THREE.TorusGeometry(r, t, 6, segs), m), 0, 0, z);
}

/** Fuselage: cylinder with two slightly darker panel-line rings. */
export function fuselage(r: number, len: number, color: string, zCenter = 0, segs = 14): THREE.Group {
  const g = group(tube(r, r, len, mat(color), zCenter, segs));
  for (const f of [-0.3, 0.3]) g.add(ring(r + 0.002, 0.006, mat(tint(color, -0.25)), zCenter + f * len, segs));
  return g;
}

/** Ogive nose: smooth tangent curve from the body radius to a point. */
export function noseOgive(r: number, len: number, color: string, zBase: number, segs = 14): THREE.Mesh {
  const pts: THREE.Vector2[] = [];
  const N = 8;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    pts.push(new THREE.Vector2(r * Math.sqrt(1 - t * t), t * len));   // elliptical ogive
  }
  pts.push(new THREE.Vector2(0, len));
  const geo = new THREE.LatheGeometry(pts, segs);
  return at(new THREE.Mesh(geo, mat(color)), 0, 0, zBase, Math.PI / 2);
}
/** Sharp cone nose. */
export function noseCone(r: number, len: number, color: string, zBase: number, segs = 14): THREE.Mesh {
  return at(new THREE.Mesh(new THREE.ConeGeometry(r, len, segs), mat(color)), 0, 0, zBase + len / 2, Math.PI / 2);
}
/** Blunt hemisphere nose. */
export function noseBlunt(r: number, color: string, zBase: number, segs = 14): THREE.Mesh {
  return at(new THREE.Mesh(new THREE.SphereGeometry(r, segs, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat(color)), 0, 0, zBase, Math.PI / 2);
}

/** Flared nozzle: dark frustum opening backward with a darker throat disc inside. */
export function nozzle(rThroat: number, rExit: number, len: number, zExit: number): THREE.Group {
  return group(
    tube(rThroat, rExit, len, mat(ORD.darkMetal), zExit + len / 2, 12),
    at(new THREE.Mesh(new THREE.CircleGeometry(rThroat * 0.9, 12), mat(ORD.throat)), 0, 0, zExit + len * 0.3, Math.PI),
  );
}

/** Swept trapezoid fin (extruded shape), root along +Z at the body surface. */
export function fin(root: number, tipChord: number, span: number, sweep: number, color: string, thickness = 0.018): THREE.Mesh {
  const s = new THREE.Shape();
  s.moveTo(0, 0); s.lineTo(root, 0); s.lineTo(root - sweep, span); s.lineTo(root - sweep - tipChord, span); s.lineTo(0, 0);
  const geo = new THREE.ExtrudeGeometry(s, { depth: thickness, bevelEnabled: false });
  geo.translate(0, 0, -thickness / 2);
  return new THREE.Mesh(geo, mat(color));
}
/** n fins around the body at radius r, roots starting at zRoot and running forward by `root`. */
export function finSet(n: number, r: number, zRoot: number, root: number, tipChord: number, span: number, sweep: number, color: string, phase = 0): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const a = phase + (i / n) * Math.PI * 2;
    const f = fin(root, tipChord, span, sweep, color);
    // shape X → +Z (chord), shape Y → radial; built in XY then oriented
    f.rotation.set(0, -Math.PI / 2, 0);        // chord along +Z, span along +Y
    const holder = new THREE.Group();
    holder.add(f);
    f.position.set(0, r * 0.98, zRoot);
    holder.rotation.z = a;
    g.add(holder);
  }
  return g;
}

/** Hazard band: dark cylinder band with four colour plates. */
export function hazardBand(r: number, z: number, w: number, color: string, segs = 14): THREE.Group {
  const g = group(tube(r + 0.004, r + 0.004, w, mat(ORD.hazard), z, segs));
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 8;
    const plate = at(new THREE.Mesh(new THREE.BoxGeometry(0.02, w * 0.8, 0.1 * r + 0.03), mat(color)), 0, 0, 0);
    plate.position.set(Math.cos(a) * (r + 0.012), Math.sin(a) * (r + 0.012), z);
    plate.rotation.z = a + Math.PI / 2; plate.rotation.y = 0; plate.rotation.x = Math.PI / 2;
    g.add(plate);
  }
  return g;
}

/** Small glowing indicator / sensor port on the hull. */
export function port(r: number, z: number, angle: number, color: string, size = 0.025): THREE.Mesh {
  return at(new THREE.Mesh(new THREE.SphereGeometry(size, 8, 6), matE(color, 0.7)), Math.cos(angle) * r, Math.sin(angle) * r, z);
}

/** Small sensor dish with a feed arm, facing +Z, hinged at its base. */
export function dish(r: number, color: string): THREE.Group {
  const m = mat(color); m.side = THREE.DoubleSide;
  return group(
    at(new THREE.Mesh(new THREE.SphereGeometry(r, 12, 6, 0, Math.PI * 2, 0, Math.PI * 0.4), m), 0, 0, 0, -Math.PI / 2),
    at(new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, r * 0.8, 4), mat(ORD.lightMetal)), 0, 0, r * 0.4, Math.PI / 2),
    at(new THREE.Mesh(new THREE.SphereGeometry(0.014, 6, 6), matE(color, 0.6)), 0, 0, r * 0.8),
  );
}

/** Short open-ended strut cage around the Z axis (n struts + two hoops). */
export function cage(r: number, len: number, n: number, color: string, zCenter: number): THREE.Group {
  const g = group(ring(r, 0.012, mat(color), zCenter - len / 2), ring(r, 0.012, mat(color), zCenter + len / 2));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    g.add(at(new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, len, 6), mat(color)), Math.cos(a) * r, Math.sin(a) * r, zCenter, Math.PI / 2));
  }
  return g;
}
